import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ShieldCheck, User, X, Save } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const StaffPanel = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/staff");
      setList(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Lengkapi semua kolom");
    setSaving(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Akun staf dibuat");
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus akun ini?")) return;
    try {
      await api.delete(`/admin/staff/${id}`);
      toast.success("Akun dihapus");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const input = "mt-2 w-full rounded-lg bg-navy-soft border border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan/50";

  return (
    <div data-testid="panel-staff">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-2xl">Akun Staf</h2>
        <button onClick={() => setForm({ name: "", email: "", password: "", role: "staff" })} data-testid="add-staff" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy hover:shadow-[0_0_24px_rgba(212,175,55,0.4)]">
          <Plus className="w-4 h-4" /> Tambah Staf
        </button>
      </div>

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-cyan" /></div>
      ) : (
        <div className="space-y-3">
          {list.map((u) => (
            <div key={u.id} data-testid="staff-row" className="flex items-center justify-between rounded-xl border border-white/10 bg-navy-card/50 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className={`grid place-items-center w-10 h-10 rounded-full ${u.role === "admin" ? "bg-gold/15 text-gold" : "bg-cyan/15 text-cyan"}`}>
                  {u.role === "admin" ? <ShieldCheck className="w-5 h-5" /> : <User className="w-5 h-5" />}
                </span>
                <div>
                  <p className="font-semibold">{u.name} {u.email === user?.email && <span className="text-xs text-slate-500">(Anda)</span>}</p>
                  <p className="text-xs text-slate-400">{u.email} · <span className="uppercase">{u.role}</span></p>
                </div>
              </div>
              {u.email !== user?.email && (
                <button onClick={() => del(u.id)} data-testid="delete-staff" className="grid place-items-center w-9 h-9 rounded-lg border border-white/10 text-slate-300 hover:text-red-400 hover:border-red-400/40"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {form && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-navy/80 backdrop-blur-sm grid place-items-center p-4" onClick={() => setForm(null)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-card p-6 md:p-8" data-testid="form-staff">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-bold text-xl">Tambah Akun Staf</h3>
                <button onClick={() => setForm(null)} className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-slate-300 hover:bg-white/10"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-4">
                <div><label className="text-sm text-slate-300 font-medium">Nama</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="staff-name" /></div>
                <div><label className="text-sm text-slate-300 font-medium">Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="staff-email" /></div>
                <div><label className="text-sm text-slate-300 font-medium">Kata Sandi (min. 6 karakter)</label><input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} data-testid="staff-password" /></div>
                <div>
                  <label className="text-sm text-slate-300 font-medium">Peran</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={input} data-testid="staff-role">
                    <option value="staff">Staf (kelola konten)</option>
                    <option value="admin">Admin (kelola konten + akun)</option>
                  </select>
                </div>
              </div>
              <div className="mt-8 flex gap-3 justify-end">
                <button onClick={() => setForm(null)} className="rounded-lg border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5">Batal</button>
                <button onClick={create} disabled={saving} data-testid="save-staff" className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-bold text-navy disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Buat Akun
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
